import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DateRangePicker,
    displayFormatTime,
    Enums,
    FormStore,
    inject,
    NothingHere,
    SubTopBar,
    TasksStore,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { RouteComponentProps } from 'react-router';
import { Form } from 'reactstrap';
import { AppStore } from '../../Core/AppStore';
import { DisplaysStore } from '../DisplaysStore';

export interface IDisplayStatusLogParams {
    serialNumber: string;
}
export interface IDisplayStatusLogProps extends RouteComponentProps<IDisplayStatusLogParams> {}

class DisplayStatusLogGroup {
    public constructor(activeDisplayStatusLog: Mri.DisplayStatuses.ActiveDisplayStatusLog) {
        this.head = activeDisplayStatusLog;
        this.startTime = activeDisplayStatusLog.pollTime;
        this.endTime = activeDisplayStatusLog.pollTime;
    }

    @bind
    @action
    public toggle(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.open = !this.open;
    }

    public push(activeDisplayStatusLog: Mri.DisplayStatuses.ActiveDisplayStatusLog) {
        this.endTime = activeDisplayStatusLog.pollTime;
        this.statusLog.push(activeDisplayStatusLog);
    }

    @observable
    public head: Mri.DisplayStatuses.ActiveDisplayStatusLog;

    @observable
    public statusLog: Mri.DisplayStatuses.ActiveDisplayStatusLog[] = [];

    @observable
    public startTime: Date;

    @observable
    public endTime: Date;

    @observable
    public open = false;
}

@autoinject
@observer
export class DisplayStatusLog extends React.Component<IDisplayStatusLogProps, any> {
    public constructor(props: IDisplayStatusLogProps) {
        super(props);
        this.state = {};

        const startTime = this.appStore.storage.displayLogTimes?.start
            ? new Date(this.appStore.storage.displayLogTimes?.start)
            : this.formStore.fields.startTime.value;
        if (!this.appStore.storage.displayLogTimes?.start) {
            startTime.setMonth(startTime.getMonth() - 1);
        } else {
            this.formStore.fields.startTime.setValue(startTime);
        }

        const endTime = this.appStore.storage.displayLogTimes?.end
            ? new Date(this.appStore.storage.displayLogTimes?.end)
            : this.formStore.fields.endTime.value;
        if (!this.appStore.storage.displayLogTimes?.end) {
            endTime.setDate(endTime.getDate() + 1);
            endTime.setMinutes(59);
            endTime.setSeconds(59);
        } else {
            this.formStore.fields.endTime.setValue(endTime);
        }
    }

    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getStatusLogAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                dateFormat="MM/dd/yyyy"
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            {!!this.groupedStatusLog?.length ? '' : 'No Results'}

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>

                            <a className="btn btn-link" onClick={this.exportCsvUrlAsync}>
                                Export to CSV
                            </a>
                        </div>
                    </Form>
                </SubTopBar>
                <table className="table table-striped fixed-header">
                    <thead>
                        <tr>
                            <th>Status Message</th>
                            <th>Poll Time</th>
                        </tr>
                    </thead>
                    <tbody>{this.groupedStatusLog.map(this.renderHeadRow).reduce((a, c) => a.concat(c), [])}</tbody>
                </table>

                <NothingHere
                    empty={!this.groupedStatusLog.length}
                    icon={<BsCardHeading />}
                    label="No Data Available"
                    loading={this.loading}
                    loadingMessage="Loading Data..."
                />
            </div>
        );
    }

    @bind
    private renderHeadRow(group: DisplayStatusLogGroup, idx: number) {
        let className = '';

        if (
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online &&
            group.head.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical
        ) {
            className = 'text-danger';
        } else if (group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.AcPowerLoss) {
            className = 'text-warning';
        } else if (group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.RecoveryServer) {
            className = 'text-danger';
        } else if (
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online &&
            group.head.displayHealth == Mri.DisplayStatuses.DisplayHealth.Warning
        ) {
            className = 'text-warning';
        } else if (
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocException ||
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.SerialNumberMismatch
        ) {
            className = 'text-info';
        } else if (
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.OfflineOrInactive ||
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.NeverCommunicated ||
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect ||
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.EngineOffTimedOut ||
            group.head.displayStatus == Mri.DisplayStatuses.DisplayStatus.TimedOut
        ) {
            className = 'text-muted';
        } else {
            className = '';
        }

        let subject = Enums.toString(Enums.DisplayStatus, group.head.displayStatus);
        if (
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.AcPowerLoss &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.RecoveryServer &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.EngineOff &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.EngineOffTimedOut &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.OfflineOrInactive &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.NeverCommunicated &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.NocException &&
            group.head.displayStatus != Mri.DisplayStatuses.DisplayStatus.SerialNumberMismatch
        ) {
            subject += ', ' + Enums.toString(Enums.DisplayHealth, group.head.displayHealth);
        }

        if (group.head.details) {
            subject += ' - ' + group.head.details;
        }

        let rows: JSX.Element[] = [];
        rows.push(
            <tr key={idx}>
                <td className={className}>{subject}</td>
                <td>
                    {!!group.statusLog.length && (
                        <span>
                            {displayFormatTime(
                                group.endTime,
                                'EEEE, MMMM do yyyy, h:mm:ss a',
                                this.props.match.params.serialNumber
                            )}
                            . Till{' '}
                        </span>
                    )}
                    {displayFormatTime(
                        group.startTime,
                        'EEEE, MMMM do yyyy, h:mm:ss a',
                        this.props.match.params.serialNumber
                    )}{' '}
                    {!!group.statusLog.length && (
                        <a href="#" onClick={group.toggle}>
                            {group.statusLog.length} Entries <b className="caret" />
                        </a>
                    )}
                </td>
            </tr>
        );

        if (group.open) {
            rows = rows.concat(group.statusLog.map(this.renderRow));
        }

        return rows;
    }

    @bind
    private renderRow(statusLog: Mri.DisplayStatuses.ActiveDisplayStatusLog) {
        return (
            <tr>
                <td
                    className={
                        statusLog.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical ? 'text-danger' : ''
                    }
                >
                    {Enums.toString(Enums.DisplayStatus, statusLog.displayStatus)},{' '}
                    {Enums.toString(Enums.DisplayHealth, statusLog.displayHealth)}
                    {statusLog.details && (
                        <span>
                            {' - '}
                            {statusLog.details}
                        </span>
                    )}
                </td>
                <td>
                    {displayFormatTime(
                        statusLog.pollTime,
                        'EEEE, MMMM do yyyy, h:mm:ss a',
                        this.props.match.params.serialNumber
                    )}
                </td>
            </tr>
        );
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getStatusLogAsync();
    }

    @action
    private async getStatusLogAsync() {
        this.loading = true;
        this.appStore.storage.displayLogTimes = {
            start: this.formStore.fields.startTime.value,
            end: this.formStore.fields.endTime.value,
        };
        try {
            const statusLog = await this.displaysStore.getStatusLogAsync(
                this.props.match.params.serialNumber,
                this.formStore.values.startTime,
                this.formStore.values.endTime
            );
            if (statusLog) {
                this.statusLog = statusLog;
            }
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async exportCsvUrlAsync() {
        const query = stringify({
            startTime: this.formStore.values.startTime,
            endTime: this.formStore.values.endTime,
        });
        const task = this.tasksStore.startSessionTask('Export Status Log');

        try {
            await this.displaysStore.exportStatusLogAsync(this.props.match.params.serialNumber, query);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @computed
    private get groupedStatusLog() {
        const groups: DisplayStatusLogGroup[] = [];
        let group: DisplayStatusLogGroup | null = null;

        for (const entry of this.statusLog) {
            if (
                group == null ||
                group.head.displayStatus != entry.displayStatus ||
                group.head.displayHealth != entry.displayHealth
            ) {
                group = new DisplayStatusLogGroup(entry);
                groups.push(group);
            } else {
                group.push(entry);
            }
        }

        return groups;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private loading = false;
    @observable
    private statusLog: Mri.DisplayStatuses.ActiveDisplayStatusLog[] = [];
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
